import React from "react"
import PropTypes from "prop-types"
import { Check } from "lucide-react"
import { cn } from "../../../lib/utils"
import "./AppStepper.scss"

/**
 * Reusable horizontal stepper for wizard-style multi-step flows.
 *
 * @param {Array}    steps       - Array of { label: string }
 * @param {number}   activeStep  - 0-indexed current step
 * @param {Function} onStepClick - Callback when a step circle is clicked
 */
function AppStepper({ steps, activeStep, onStepClick }) {
  return (
    <nav className="app-stepper" aria-label="Progress" data-testid="app-stepper">
      {steps.map((step, index) => {
        const isCompleted = index < activeStep
        const isActive = index === activeStep
        const isUpcoming = index > activeStep

        return (
          <React.Fragment key={step.label}>
            {/* Step circle + label */}
            <button
              type="button"
              className={cn(
                "app-stepper-step",
                isCompleted && "completed",
                isActive && "active",
                isUpcoming && "upcoming",
              )}
              onClick={() => onStepClick?.(index)}
              aria-current={isActive ? "step" : undefined}
              aria-label={`Step ${index + 1}: ${step.label}`}
              data-testid={`stepper-step-${index}`}
            >
              <span className="app-stepper-number">
                {isCompleted ? (
                  <Check size={14} strokeWidth={3} />
                ) : (
                  index + 1
                )}
              </span>
              <span className="app-stepper-label">{step.label}</span>
            </button>

            {/* Connector line (skip after last step) */}
            {index < steps.length - 1 && (
              <div
                className={cn(
                  "app-stepper-connector",
                  index < activeStep && "done",
                )}
              />
            )}
          </React.Fragment>
        )
      })}
    </nav>
  )
}

AppStepper.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  activeStep: PropTypes.number.isRequired,
  onStepClick: PropTypes.func,
}

export default AppStepper
